import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { Star, TrendingUp, Clock, Tag, CheckCircle } from "lucide-react";
import { MarketplaceCard } from "@/components/ui/marketplace-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface OfferCardProps {
  id: string;
  title: string;
  price: number;
  dr: number | null;
  da: number | null;
  traffic: number | null;
  linkType: string | null;
  turnaroundDays: number | null;
  tags: string[];
  sellerName: string;
  sellerId: string;
  sellerRating: number;
  sellerReviewCount: number;
  slug?: string;
  sellerSlug?: string;
  sellerVerified?: boolean;
  sellerAvatar?: string;
}

const OfferCard = ({ 
  id, 
  title, 
  price, 
  dr, 
  da, 
  traffic, 
  linkType, 
  turnaroundDays,
  tags,
  sellerName,
  sellerId,
  sellerRating,
  sellerReviewCount,
  slug,
  sellerSlug,
  sellerVerified = false,
  sellerAvatar
}: OfferCardProps) => {
  const navigate = useNavigate();
  
  // Use the URL slug if available, otherwise fallback to ID
  const offerUrl = slug ? `/offers/${slug}` : `/offers/${id}`;
  const sellerUrl = sellerSlug ? `/sellers/${sellerSlug}` : `/sellers/${sellerId}`;

  // Handle offer click with a forced navigation to ensure new component mount
  const handleOfferClick = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log(`Navigating to offer: ${offerUrl}`);
    // Force a navigation which will cause a component remount
    navigate(offerUrl, { replace: false });
  };

  // Title component with link
  const titleComponent = (
    <a href={offerUrl} className="hover:underline" onClick={handleOfferClick}>
      <h3 className="font-semibold text-sm min-h-[2.5rem] line-clamp-2">
        {title}
      </h3>
    </a>
  );

  // Create metrics array with conditional items
  const metrics = [];
  
  if (dr !== null) {
    metrics.push({
      value: dr,
      label: "DR"
    });
  }
  
  if (da !== null) {
    metrics.push({
      value: da,
      label: "DA"
    });
  }
  
  if (traffic !== null) {
    metrics.push({
      value: (
        <>
          <TrendingUp className="h-3 w-3 inline mr-0.5" />
          {traffic >= 1000 ? `${(traffic / 1000).toFixed(1)}K` : traffic}
        </>
      ),
      label: "Traffic"
    });
  }
  
  if (linkType) {
    metrics.push({
      value: (
        <>
          <Tag className="h-3 w-3 inline mr-0.5" />
          {linkType}
        </>
      ),
      label: "Type",
      tooltip: linkType
    });
  }
  
  // Price display section
  const priceSection = (
    <div className="flex items-center justify-between mb-3 w-full overflow-hidden mt-2">
      <span className="text-base font-bold">${price.toFixed(2)}</span>
      {turnaroundDays && (
        <div className="flex items-center text-xs text-gray-500 flex-shrink-0">
          <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
          <span className="truncate">{turnaroundDays} day{turnaroundDays !== 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
  
  // Tags components
  const tagsSection = tags && tags.length > 0 && (
    <div className="flex flex-wrap gap-1 mb-1 mt-1 overflow-hidden">
      {tags.slice(0, 3).map((tag, index) => (
        <Badge key={index} variant="outline" className="bg-primary/5 text-xs py-0 h-5 truncate max-w-full">
          {tag}
        </Badge>
      ))}
    </div>
  );
  
  // Seller info - updated to match CustomPackageCard style
  const sellerInfoComponent = (
    <Link 
      to={sellerUrl} 
      className="flex items-center gap-1 hover:bg-gray-50 p-1 rounded-md mt-2 w-full overflow-hidden"
    >
      <Avatar className="h-5 w-5 flex-shrink-0">
        <AvatarImage src={sellerAvatar} alt={sellerName} />
        <AvatarFallback>{sellerName.substring(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <span className="text-xs font-medium text-gray-700 flex items-center gap-1 min-w-0 truncate">
        {sellerName}
        {sellerVerified && (
          <CheckCircle className="h-3 w-3 text-blue-500 flex-shrink-0" />
        )}
      </span>
      {sellerReviewCount > 0 && (
        <div className="ml-auto flex items-center text-xs text-amber-500 flex-shrink-0">
          <Star className="fill-amber-500 h-3 w-3 mr-0.5" />
          <span>{sellerRating.toFixed(1)}</span>
          <span className="text-gray-400 ml-1">({sellerReviewCount})</span>
        </div>
      )}
    </Link>
  );

  return (
    <MarketplaceCard
      title={titleComponent}
      metrics={metrics}
      metricsColumns={metrics.length > 2 ? 2 : (metrics.length as 2 | 3 | 4)}
      bottomInfo={sellerInfoComponent}
      linkTo={offerUrl}
      actionLabel="View Details"
    >
      {priceSection}
      {tagsSection}
    </MarketplaceCard>
  );
};

export default OfferCard;
